import React from 'react';
import { Button } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';
import Table from '../../../commonComponents/Table';
import TablePagination from '../../../commonComponents/Pagination';
import {
  TableCard,
  TableTitle,
  MerchantName,
  MerchantEmail,
} from './Styles';
import { TABLE_TITLE, TABLE_COLUMNS, BUTTON_LABELS } from './constants';
import type { MerchantEarningsRow, MerchantsTableProps } from './types';

const MerchantsTable: React.FC<MerchantsTableProps> = ({
  filteredRows,
  pagination,
  loading,
  formatCurrency,
  onViewDetails,
  onPaginationChange,
}) => {
  const columns: ColumnsType<MerchantEarningsRow> = [
    {
      title: TABLE_COLUMNS.MERCHANT,
      key: 'merchant',
      render: (_, r) => (
        <>
          <MerchantName>{r.merchantName}</MerchantName>
          <MerchantEmail>{r.email}</MerchantEmail>
        </>
      ),
    },
    {
      title: TABLE_COLUMNS.REVENUE,
      dataIndex: 'totalRevenue',
      key: 'totalRevenue',
      align: 'right',
      render: (v: number) => formatCurrency(v),
    },
    {
      title: TABLE_COLUMNS.FEES,
      dataIndex: 'totalFees',
      key: 'totalFees',
      align: 'right',
      render: (v: number) => formatCurrency(v),
    },
    {
      title: TABLE_COLUMNS.NET_EARNINGS,
      dataIndex: 'netEarnings',
      key: 'netEarnings',
      align: 'right',
      render: (v: number) => formatCurrency(v),
    },
    {
      title: TABLE_COLUMNS.ORDERS,
      dataIndex: 'orderCount',
      key: 'orderCount',
      align: 'right',
      render: (v: number) => (v ?? 0).toLocaleString(),
    },
    {
      title: TABLE_COLUMNS.ACTIONS,
      key: 'actions',
      render: (_, r) => (
        <Button
          type="link"
          size="small"
          icon={<FontAwesomeIcon icon={faEye} />}
          onClick={() => onViewDetails(r)}
        >
          {BUTTON_LABELS.VIEW}
        </Button>
      ),
    },
  ];

  const dataSource = filteredRows.map((r, i) => ({ ...r, key: r.merchantId || String(i) }));
  const totalItems = pagination?.totalItems ?? 0;
  const currentPage = pagination?.currentPage ?? 1;
  const itemsPerPage = pagination?.itemsPerPage ?? 20;

  return (
    <TableCard>
      <TableTitle>{TABLE_TITLE}</TableTitle>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey="merchantId"
        loading={loading}
        pagination={false}
        emptyText="No merchants"
        scroll={{ x: 900 }}
      />
      {pagination && totalItems > 0 && (
        <TablePagination
          totalCount={totalItems}
          currentPage={currentPage}
          pageSize={itemsPerPage}
          onChange={onPaginationChange}
          totalLabel="Total merchants"
          pageSizeOptions={[10, 20, 30, 50]}
          defaultPageSize={20}
        />
      )}
    </TableCard>
  );
};

export default React.memo(MerchantsTable);
